import type {
  ConditionCmp,
  ConditionEvalNode,
  ConditionFn
} from '@axilon-apps/domains-ontology-ui-state'

/**
 * Trigger leaves carry a soup of internal fields (fn, window, baseline_fn,
 * baseline_window, metric, cmp, current_value, agg_value, baseline,
 * metric_value, value). For floor operators those labels are jargon — they
 * just want to know "what is this measuring, what fires it, where is it
 * right now?". This module composes those raw fields into three short
 * lines plus a one-glance brief suitable for the collapsed-row meta.
 */

const FN_LABEL: Record<ConditionFn, string> = {
  avg: 'mean',
  min: 'min',
  max: 'max',
  last: 'latest',
  stddev: 'stddev',
  delta: 'change',
  sum: 'sum',
  count: 'count'
}

const CMP_GLYPH: Record<ConditionCmp, string> = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  eq: '=',
  ne: '≠'
}

const METRIC_LABEL: Record<string, string> = {
  pct_from_baseline: '% from baseline',
  zscore: 'z-score',
  abs_diff: 'abs. change from baseline'
}

function fnLabel(fn?: ConditionFn): string {
  return fn ? FN_LABEL[fn] : 'value'
}

function cmpGlyph(cmp?: ConditionCmp): string {
  return cmp ? CMP_GLYPH[cmp] : '?'
}

function metricLabel(metric?: string): string {
  if (!metric) return 'deviation'
  return METRIC_LABEL[metric] ?? metric
}

function fmtNumber(n: number | undefined): string {
  if (n === undefined || !Number.isFinite(n)) return '—'
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 })
}

function fmtScalar(v: number | string | boolean | undefined): string {
  if (v === undefined) return '—'
  if (typeof v === 'boolean') return String(v)
  if (typeof v === 'string') return JSON.stringify(v)
  return fmtNumber(v)
}

function numericValue(v: number | string | boolean | undefined): number | undefined {
  return typeof v === 'number' ? v : undefined
}

function deadbandSuffix(node: ConditionEvalNode): string {
  return node.deadband === undefined ? '' : ` · deadband ${fmtNumber(node.deadband)}`
}

function isBooleanTagType(tagType?: string): boolean {
  return tagType === 'Boolean' || tagType === 'boolean'
}

function missingNow(node: ConditionEvalNode): string {
  const reason = node.reason?.trim()
  return reason || '—'
}

/** Compact current value for a collapsed leaf row, or null when none. */
export function formatLeafCurrent(node: ConditionEvalNode): string | null {
  if (node.op === 'alarm_active') {
    if (node.state === 'fired') return 'active'
    if (node.state === 'not_fired') return 'not active'
    return null
  }
  if (node.op === 'expr' && node.metric_value !== undefined && Number.isFinite(node.metric_value)) {
    return fmtNumber(node.metric_value)
  }
  const current = node.current_value
  if (typeof current === 'boolean') return String(current)
  if (typeof current === 'string') return JSON.stringify(current)
  if (current !== undefined && Number.isFinite(current)) {
    if (isBooleanTagType(node.tag_type)) {
      return current === 0 ? 'false' : 'true'
    }
    return fmtNumber(current)
  }
  if (node.agg_value !== undefined && Number.isFinite(node.agg_value)) {
    return fmtNumber(node.agg_value)
  }
  return null
}

function fmtValueWithUnit(n: number | undefined, metric?: string): string {
  if (n === undefined || !Number.isFinite(n)) return '—'
  const formatted = fmtNumber(n)
  if (metric === 'pct_from_baseline') return `${formatted}%`
  if (metric === 'zscore') return `${formatted}σ`
  return formatted
}

function signalOf(node: ConditionEvalNode): string {
  if (!node.tag_path) return 'value'
  const stripped = node.tag_path.replace(/^\[[^\]]*\][^/]*\//, '')
  const segments = stripped.split('/').filter(Boolean)
  return segments[segments.length - 1] ?? stripped
}

export interface LeafDescription {
  /** One-line summary for the collapsed-row meta (e.g. "count over 60s"). */
  brief: string
  /** What it watches (e.g. "count of CapsApplied over 60s"). */
  watching: string
  /** Threshold check (e.g. "count ≤ 0"). */
  firesWhen: string
  /** Current observed value + context (e.g. "count = 100"). */
  currentNow: string
  /** Optional extra context line for deviation triggers (current vs baseline raw values). */
  currentContext?: string
}

/**
 * Compose human-readable descriptions for an evaluated trigger leaf. Each
 * field returns a single short string; callers can place them in their
 * own labeled rows or compose them inline.
 */
export function describeLeafCondition(node: ConditionEvalNode): LeafDescription | null {
  const signal = signalOf(node)
  const cmp = cmpGlyph(node.cmp)
  const threshold = node.value

  if (node.op === 'live') {
    return {
      brief: 'live value',
      watching: `${signal} (live value)`,
      firesWhen: `value ${cmp} ${fmtScalar(threshold)}${deadbandSuffix(node)}`,
      currentNow: formatLeafCurrent(node) ?? missingNow(node)
    }
  }

  if (node.op === 'agg') {
    const fn = fnLabel(node.fn)
    const window = node.window ?? '?'
    return {
      brief: `${fn} over ${window}`,
      watching: `${fn} of ${signal} over ${window}`,
      firesWhen: `${fn} ${cmp} ${fmtNumber(numericValue(threshold))}${deadbandSuffix(node)}`,
      currentNow:
        node.agg_value !== undefined && Number.isFinite(node.agg_value)
          ? `${fn} = ${fmtNumber(node.agg_value)}`
          : missingNow(node)
    }
  }

  if (node.op === 'deviation') {
    const metric = metricLabel(node.metric)
    const baseFn = fnLabel(node.baseline_fn)
    const baseWindow = node.baseline_window ?? node.window ?? '?'
    const ctxParts: string[] = []
    if (node.agg_value !== undefined) ctxParts.push(`now ${fmtNumber(node.agg_value)}`)
    if (node.baseline !== undefined) ctxParts.push(`baseline ${fmtNumber(node.baseline)}`)
    return {
      brief: `${metric} vs ${baseWindow} ${baseFn}`,
      watching: `${signal} deviation from its ${baseWindow} ${baseFn} baseline`,
      firesWhen: `${metric} ${cmp} ${fmtValueWithUnit(numericValue(threshold), node.metric)}${deadbandSuffix(node)}`,
      currentNow: `${metric} = ${fmtValueWithUnit(node.metric_value, node.metric)}`,
      currentContext: ctxParts.length > 0 ? ctxParts.join(' · ') : undefined
    }
  }

  if (node.op === 'alarm_active') {
    const alarm = node.alarm_name?.trim() || 'alarm'
    return {
      brief: 'alarm active',
      watching: `${signal} · ${alarm}`,
      firesWhen: `${alarm} is active`,
      currentNow: formatLeafCurrent(node) ?? missingNow(node)
    }
  }

  if (node.op === 'expr') {
    const expr = node.expression?.trim() || 'expression'
    return {
      brief: expr,
      watching: expr,
      firesWhen: `${expr} ${cmp} ${fmtNumber(numericValue(threshold))}${deadbandSuffix(node)}`,
      currentNow:
        node.metric_value !== undefined && Number.isFinite(node.metric_value)
          ? `${expr} = ${fmtNumber(node.metric_value)}`
          : missingNow(node)
    }
  }

  return null
}
